import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import {
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Divider,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Header from '../Header/Header';
import Header1 from '../Header/Header1';
import Footer2 from './Footer2';
import axios from 'axios';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

const faqs = [
  {
    question: "What is Vimitra?",
    answer: "Vimitra is a dynamic knowledge exchange platform that connects people across different regions and languages to share and gain insights in their areas of interest. It supports real-time knowledge exchange through live interactive sessions, making learning more engaging and impactful.",
  },
  {
    question: "Why choose Anthrasync?",
    answer: "At Anthrasync, we believe that the free flow of knowledge empowers individuals and communities. Our platform is designed to facilitate accessible learning and meaningful sharing for everyone.",
  },
  {
    question: "Who can use the platform?",
    answer: "Anyone aged 15 and above can join Anthrasync. While the current version is tailored for users in India, we are actively working towards making it available globally.",
  },
  {
    question: "Who benefits from Anthrasync?",
    answer: "Anyone passionate about learning or sharing knowledge—be it students, professionals, educators, or hobbyists—can benefit from the platform.",
  },
  {
    question: "Can users exchange knowledge in any language?",
    answer: "Absolutely! Anthrasync is a multilingual platform that supports knowledge exchange in various Indian languages, with plans to expand further.",
  },
];

const API_URL = process.env.REACT_APP_API_URL;

function Faqs({ onLogout }) {
  const { userId } = useAuth();
  const [userQuestion, setUserQuestion] = useState('');
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/create_asked_questions/`, {
        question: userQuestion,
        user_id: userId,
      });
      alert('Your question has been submitted successfully!');
      setUserQuestion('');
    } catch (error) {
      console.error("Error submitting the question:", error);
      alert("There was an error submitting your question. Please try again.");
    }
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <Box sx={{ background: 'linear-gradient(to right, #000000, #000000ff)', minHeight: '100vh' }}>
      {userId ? (
        <Header1 userId={userId} onLogout={onLogout} />
      ) : (
        <Header />
      )}

      <Box
        sx={{
          maxWidth: 850,
          mx: 'auto',
          mt:7,
          px: { xs: 2, sm: 3 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            letterSpacing: 6,
            color: '#fff',
            background: 'linear-gradient(to right, #1E90FF, #00CFFF)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          FAQs
        </Typography>

        {faqs.map((faq, index) => (
          <Accordion
            key={index}
            sx={{
              mb: 2,
              backdropFilter: 'blur(10px)',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              color: 'white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              '&:hover': {
                borderColor: '#1E90FF',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#1E90FF' }} />}
              aria-controls={`panel-${index}-content`}
              id={`panel-${index}-header`}
            >
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 400, letterSpacing: 1 }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <Divider sx={{ backgroundColor: '#333' }} />
            <AccordionDetails>
              <Typography sx={{ color: '#ccc', letterSpacing: 1 }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Ask a Question Section */}
      {userId && (
        <Box
          sx={{
            maxWidth: 850,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            py: 4,
            mb: 10,
            borderRadius: 3,
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            '&:hover': {
              borderColor: '#00CFFF',
            },
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: '#fff',
              fontWeight: 600,
              letterSpacing: 3,
              textAlign: 'center',
              mb: 3,
            }}
          >
            Ask Us Your Question
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Your Question"
              variant="outlined"
              value={userQuestion}
              onChange={(e) => setUserQuestion(e.target.value)}
              multiline
              rows={3}
              sx={{
                mb: 2,
                backgroundColor: '#222',
                borderRadius: 1,
                input: { color: '#fff' },
                label: { color: '#aaa' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#444',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1E90FF',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00CFFF',
                  },
                },
              }}
            />
            <Box textAlign="center">
              <Button
                type="submit"
                variant="contained"
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: '1rem',
                  background: 'linear-gradient(to right, #1E90FF, #00CFFF)',
                  color: '#fff',
                  fontWeight: 600,
                  letterSpacing: 2,
                  borderRadius: 8,
                  '&:hover': {
                    background: 'linear-gradient(to right, #00CFFF, #1E90FF)',
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      )}

      {/* Scroll to Top Button */}
      <Tooltip title="Scroll to Top" placement="top">
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(to right, #1E90FF, #00CFFF)',
            color: '#fff',
            boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
            '&:hover': {
              background: 'linear-gradient(to right, #00CFFF, #1E90FF)',
            },
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
      </Tooltip>

      <Footer2 />
    </Box>
  );
}

export default Faqs;
